import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApproveLeaveRequestDTO,
  CreateLeaveEntitlementDTO,
  CreateLeaveRequestDTO,
  CreateLeaveTypeDTO,
  ListLeaveEntitlementsQuery,
  ListLeaveRequestsQuery,
  ListLeaveTypesQuery,
  UpdateLeaveEntitlementDTO,
  UpdateLeaveRequestDTO,
  UpdateLeaveTypeDTO,
} from "@/modules/hr/leave/domain/types";
import { leaveApi } from "./client";

const keys = {
  types: (q: ListLeaveTypesQuery) => ["hr", "leave", "types", q] as const,
  typeDetail: (id: string) => ["hr", "leave", "types", "detail", id] as const,
  entitlements: (q: ListLeaveEntitlementsQuery) =>
    ["hr", "leave", "entitlements", q] as const,
  entitlementDetail: (id: string) =>
    ["hr", "leave", "entitlements", "detail", id] as const,
  requests: (q: ListLeaveRequestsQuery) =>
    ["hr", "leave", "requests", q] as const,
  requestDetail: (id: string) =>
    ["hr", "leave", "requests", "detail", id] as const,
};

export function useLeaveTypesQuery(query: ListLeaveTypesQuery) {
  return useQuery({
    queryKey: keys.types(query),
    queryFn: () => leaveApi.listTypes(query),
  });
}

export function useLeaveTypeDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.typeDetail(id ?? ""),
    queryFn: () => leaveApi.getTypeById(id!),
    enabled: !!id,
  });
}

export function useCreateLeaveTypeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateLeaveTypeDTO) => leaveApi.createType(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useUpdateLeaveTypeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateLeaveTypeDTO }) =>
      leaveApi.updateType(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useDeleteLeaveTypeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => leaveApi.deleteType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useLeaveEntitlementsQuery(
  query: ListLeaveEntitlementsQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: keys.entitlements(query),
    queryFn: () => leaveApi.listEntitlements(query),
    enabled: options?.enabled ?? true,
  });
}

export function useLeaveEntitlementDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.entitlementDetail(id ?? ""),
    queryFn: () => leaveApi.getEntitlementById(id!),
    enabled: !!id,
  });
}

export function useCreateLeaveEntitlementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateLeaveEntitlementDTO) =>
      leaveApi.createEntitlement(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useUpdateLeaveEntitlementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateLeaveEntitlementDTO }) =>
      leaveApi.updateEntitlement(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useLeaveRequestsQuery(
  query: ListLeaveRequestsQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: keys.requests(query),
    queryFn: () => leaveApi.listRequests(query),
    enabled: options?.enabled ?? true,
  });
}

export function useLeaveRequestDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.requestDetail(id ?? ""),
    queryFn: () => leaveApi.getRequestById(id!),
    enabled: !!id,
  });
}

export function useApproveLeaveRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: ApproveLeaveRequestDTO & { id: string }) =>
      leaveApi.approveRequest(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useApproveLeaveRequestLineMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: ApproveLeaveRequestDTO & { id: string }) =>
      leaveApi.approveRequestLine(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useRejectLeaveRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      rejectionReason,
    }: {
      id: string;
      rejectionReason?: string | null;
    }) => leaveApi.rejectRequest(id, { rejectionReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useDeleteLeaveRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => leaveApi.deleteRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useBulkApproveLeaveRequestsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => leaveApi.bulkApprove(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useBulkRejectLeaveRequestsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      rejectionReason,
    }: {
      ids: string[];
      rejectionReason?: string | null;
    }) => leaveApi.bulkReject(ids, rejectionReason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useBulkDeleteLeaveRequestsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => leaveApi.bulkDelete(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useCreateLeaveRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateLeaveRequestDTO) => leaveApi.createRequest(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useUpdateLeaveRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateLeaveRequestDTO }) =>
      leaveApi.updateRequest(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "leave"] });
    },
  });
}

export function useMyEntitlementsQuery(year: number) {
  return useQuery({
    queryKey: ["hr", "leave", "my-entitlements", year] as const,
    queryFn: () => leaveApi.listMyEntitlements(year),
    enabled: true,
  });
}
